import React from 'react'
import {ClassNames, css, keyframes, useTheme} from '@emotion/react'
import Dropzone, {ErrorCode} from 'react-dropzone'
import {Typography, Snackbar, Alert} from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'

import PreviewList from './PreviewList'
import {convertBytesToMbsOrKbs, readFile} from './helpers'
import {useTranslations} from 'next-intl'

const progress = keyframes`
  0% {
    background-position: 0 0;
  }

  100% {
    background-position: -70px 0;
  }
`

export default function FileDropzone({
                                       className,
                                       acceptedFiles = [],
                                       onAlert,
                                       onChange,
                                       onDrop,
                                       onDropRejected,
                                       onDelete,
                                       filesLimit = 1,
                                       maxFileSize = 3000000,

                                       ...props
                                     }) {
  const theme = useTheme()
  const styles = {
    root: css`
      padding: 8px;
      
      display: flex;
      justify-content: center;
      align-items: center;

      min-height: 200px;
      background-color: ${theme.palette.surface.main};

      border-style: dashed;
      border-color: ${theme.palette.primary.main};
      border-radius: 8px;

      box-sizing: border-box;
      cursor: pointer;

      overflow: hidden;

      &:hover {
        background: ${theme.palette.surface.dark};
      }
    `,

    active: css`
      animation: ${progress} 2s linear infinite !important;
      background-image: repeating-linear-gradient(-45deg, ${theme.palette.primary.light}, ${theme.palette.primary.light} 25px, ${theme.palette.primary.dark} 25px, ${theme.palette.primary.dark} 50px);
      background-size: 150% 100%;
      border-style: solid;
      border-color: ${theme.palette.primary.light};
    `,

    invalid: {
      // eslint-disable-next-line max-len
      backgroundImage: `repeating-linear-gradient(-45deg, ${theme.palette.error.light}, ${theme.palette.error.light} 25px, ${theme.palette.error.dark} 25px, ${theme.palette.error.dark} 50px)`,
      borderColor: theme.palette.error.main,
    },
    textContainer: css`
      text-align: center;
    `,
    preview: {},
    text: css`
      margin-top: 8px;
      margin-bottom: 8px;

      @media (max-width: ${theme.breakpoints.sm}px) {
        font-size: 1rem;
      }
    `,
    icon: css`
      width: 51px;
      height: 51px;
    `,
    resetButton: css`
      display: block;
      margin: 10px 0;
    `,
  }


  const t = useTranslations()
  const [snackbarState, setSnackbarState] = React.useState({
    openSnackBar: false,
    snackbarMessage: '',
    snackbarVariant: 'success',
  })
  const [fileObjects, setFileObjects] = React.useState([])

  React.useEffect(() => {
    if (onChange) {
      onChange(fileObjects.map((fileObject) => fileObject.file))
    }
  }, [onChange, fileObjects])

  const addFiles = async (newFileObjects) => {
    // Update component state
    setFileObjects((fileObjects) => {
      // Handle a single file
      if (filesLimit <= 1) {
        return [].concat(newFileObjects[0])
      }

      // Handle multiple files
      return [].concat(
        fileObjects,
        newFileObjects
      )
    })
  }

  const deleteFile = (removedFileObj, removedFileObjIdx) => {
    event.stopPropagation()

    // Calculate remaining fileObjects array
    const remainingFileObjs = fileObjects.filter((fileObject, i) => {
      return i !== removedFileObjIdx
    })

    // Notify removed file
    if (onDelete) {
      onDelete(removedFileObj.file, removedFileObjIdx)
    }

    // Update local state
    setFileObjects(remainingFileObjs)
  }

  React.useEffect(() => {
    if (snackbarState.openSnackBar && onAlert) {
      onAlert(snackbarState.snackbarMessage, snackbarState.snackbarVariant)
    }
  }, [snackbarState.openSnackBar, snackbarState.snackbarMessage, snackbarState.snackbarVariant, onAlert])

  const getFileLimitExceedMessage = (filesLimit) => t('file-dropzone.max-allowed-error', {filesLimit})

  const getFileAddedMessage = (fileName) => t('file-dropzone.success-message', {fileName})

  const getFileRemovedMessage = (fileName) => t('file-dropzone.removed-message', {fileName})

  const getDropRejectMessage = (rejectedFile) => {
    let message = t('file-dropzone.rejected-message', {fileName: rejectedFile.file.name})
    for (const error of rejectedFile.errors) {
      if (error.code === ErrorCode.FileTooLarge) {
        message += ' - ' + t('file-dropzone.large-file-error', {fileSize: maxFileSize})
      } else {
        message += ' - ' + error.message
      }
    }
    return message
  }

  const handleDropAccepted = async (acceptedFiles, evt) => {
    if (filesLimit > 1 && fileObjects.length + acceptedFiles.length > filesLimit) {
      setSnackbarState({
        openSnackBar: true,
        snackbarMessage: getFileLimitExceedMessage(filesLimit),
        snackbarVariant: 'error',
      })
      return
    }

    // Notify Drop event
    if (onDrop) {
      onDrop(acceptedFiles, evt)
    }

    // Retrieve fileObjects data
    const fileObjs = await Promise.all(
      acceptedFiles.map(async (file) => {
        const data = await readFile(file)
        return {
          file,
          data,
        }
      })
    )

    // Notify added files
    await addFiles(fileObjs)

    // Display message
    const message = fileObjs.reduce((msg, fileObj) => msg + getFileAddedMessage(fileObj.file.name), '')
    setSnackbarState({
      openSnackBar: true,
      snackbarMessage: message,
      snackbarVariant: 'success',
    })
  }

  const handleDropRejected = (rejectedFiles, evt) => {
    let message = ''
    if (fileObjects.length + rejectedFiles.length > filesLimit) {
      message = getFileLimitExceedMessage(filesLimit)
    } else {
      rejectedFiles.forEach((rejectedFile) => {
        message = getDropRejectMessage(rejectedFile)
      })
    }

    if (onDropRejected) {
      onDropRejected(rejectedFiles, evt)
    }

    setSnackbarState({
      openSnackBar: true,
      snackbarMessage: message,
      snackbarVariant: 'error',
    })
  }

  const handleRemove = (fileIndex) => (event) => {
    event.stopPropagation()

    // Find removed fileObject
    const removedFileObj = fileObjects[fileIndex]

    // Notify removed file
    deleteFile(removedFileObj, fileIndex)

    setSnackbarState({
      openSnackBar: true,
      snackbarMessage: getFileRemovedMessage(removedFileObj.file.name),
      snackbarVariant: 'info',
    })
  }

  const handleCloseSnackbar = () => {
    setSnackbarState({
      openSnackBar: false,
    })
  }

  const acceptFiles = acceptedFiles?.join(',')
  const isMultiple = filesLimit > 1
  const previewsVisible = fileObjects.length > 0

  return (
    <>
      <Dropzone
        accept={acceptFiles}
        onDropAccepted={handleDropAccepted}
        onDropRejected={handleDropRejected}
        maxSize={maxFileSize}
        multiple={isMultiple}
      >
        {({getRootProps, getInputProps, isDragActive, isDragReject}) => (
          <ClassNames>
            {
              ({css}) => (
                <div {...getRootProps({
                  className: css(
                    styles.root,
                    isDragActive && styles.active,
                    (isDragReject) && styles.invalid,
                    className
                  ),
                  ...props
                })}
                >
                  <input {...getInputProps()} />

                  {previewsVisible ? (
                    <PreviewList
                      css={styles.preview}
                      fileObjects={fileObjects}
                      handleRemove={handleRemove}
                    />
                  ) : (
                    <div css={styles.textContainer}>
                      <Typography css={styles.text} variant="h5" component="p">
                        {t('file-dropzone.dropzone')}
                      </Typography>
                      <CloudUploadIcon css={styles.icon}/>
                    </div>
                  )}
                </div>
              )
            }

          </ClassNames>
        )}
      </Dropzone>

      <Snackbar
        anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
        autoHideDuration={6000}
        open={snackbarState.openSnackBar}
        onClose={handleCloseSnackbar}
      >
        <Alert
          severity={snackbarState.snackbarVariant}
          onClose={handleCloseSnackbar}
        >{snackbarState.snackbarMessage}</Alert>
      </Snackbar>
    </>
  )
}
